import type { PagedMetadata, SortDirection } from './types';

/**
 * The API pages from 1 and MUI's data grid pages from 0. Every conversion goes through here, because
 * an off-by-one shows the wrong page without failing anything.
 */

export interface GridPaginationModel {
  /** Zero-based, as the data grid counts. */
  page: number;
  pageSize: number;
}

/** Zero-based grid page → one-based API page. */
export function toApiPage(gridPage: number): number {
  return Math.max(0, Math.trunc(gridPage)) + 1;
}

/** One-based API page → zero-based grid page. */
export function toGridPage(apiPage: number): number {
  return Math.max(0, Math.trunc(apiPage) - 1);
}

/** The grid model a response should leave the table showing. */
export function toGridPaginationModel(metadata: PagedMetadata): GridPaginationModel {
  return { page: toGridPage(metadata.currentPage), pageSize: metadata.pageSize };
}

export interface GridSortItem {
  field: string;
  sort?: 'asc' | 'desc' | null;
}

export interface ApiSort {
  sortBy?: string;
  sortDirection?: SortDirection;
}

/**
 * First sort item the API understands. Unknown fields are dropped rather than sent, since the API
 * silently falls back to its default ordering and the table would claim a sort it does not have.
 */
export function toApiSort(model: readonly GridSortItem[], allowed: readonly string[]): ApiSort {
  const item = model[0];
  if (!item || !item.sort || !allowed.includes(item.field)) return {};

  return { sortBy: item.field, sortDirection: item.sort };
}

// ---------------------------------------------------------------------------
// Flowis gate paging
// ---------------------------------------------------------------------------

/**
 * The gate pages by offset and returns absolute `next`/`previous` URLs on its own host. Those URLs
 * are never fetched — they carry neither our bearer token nor the account header — so offsets are
 * computed here and the URLs are read only as has-more flags.
 */
export function gateOffsetForPage(page: number, limit: number): number {
  const safePage = Math.max(0, Math.trunc(page));
  const safeLimit = Math.max(1, Math.trunc(limit));
  return safePage * safeLimit;
}

export function gatePageFromOffset(offset: number, limit: number): number {
  const safeLimit = Math.max(1, Math.trunc(limit));
  return Math.floor(Math.max(0, Math.trunc(offset)) / safeLimit);
}

/**
 * Offset to keep when the page size changes: the first row currently on screen stays on screen,
 * rounded down to the new page boundary.
 */
export function gateOffsetForNewLimit(currentOffset: number, newLimit: number): number {
  const safeLimit = Math.max(1, Math.trunc(newLimit));
  return Math.floor(Math.max(0, Math.trunc(currentOffset)) / safeLimit) * safeLimit;
}
